import { CustomFieldItemDto } from '@artemis-software/wr-api';

export interface VisibilityNode {
  fieldId: string;
  valueType: string;
  visibleIfFieldId: string | null;
  visibleIfValue: string | null;
  hasBaselineValue: boolean;
  /** True when the member was removed from the collection — the node still renders, but as a tombstone. */
  memberDeleted: boolean;
}

const matchesTrigger = (trigger: VisibilityNode, value: unknown, expected: string | null): boolean => {
  if (expected === null || expected === '') return false;
  if (trigger.valueType === 'Boolean') return value === (expected === 'true');
  return value === expected;
};

/**
 * Field ids currently hidden by a visibility rule (WR-2292).
 *
 * Two escape hatches keep this from ever swallowing data: fail-open when the trigger is not part
 * of the same collection, and grandfathering when the dependent field already held a value at load
 * time — a rule added afterwards must not retroactively hide what a previous inspection captured.
 *
 * `getValue` returns the UNWRAPPED control value (boolean for Ja/Nein, string for Auswahl).
 */
export function computeHiddenFieldIds(
  nodes: VisibilityNode[],
  getValue: (fieldId: string) => unknown,
): Set<string> {
  // Only a member still in the collection can act as a trigger. A field present solely as a
  // tombstone is not part of the group any more, so a rule pointing at it finds nothing and falls
  // open — the same rule the backend and the app evaluators apply. Building the lookup from live
  // nodes only also removes the dependency on list order, which decided the winner before.
  const byField = new Map(nodes.filter(node => !node.memberDeleted).map(node => [node.fieldId, node]));
  const cache = new Map<string, boolean>();

  const isVisible = (node: VisibilityNode, seen: Set<string>): boolean => {
    const cached = cache.get(node.fieldId);
    if (cached !== undefined) return cached;
    if (seen.has(node.fieldId)) return true;
    seen.add(node.fieldId);

    let visible: boolean;
    if (!node.visibleIfFieldId || node.hasBaselineValue) {
      visible = true;
    } else {
      const trigger = byField.get(node.visibleIfFieldId);
      visible = !trigger
        || (isVisible(trigger, seen) && matchesTrigger(trigger, getValue(trigger.fieldId), node.visibleIfValue));
    }
    cache.set(node.fieldId, visible);
    return visible;
  };

  const hidden = new Set<string>();
  nodes.forEach(node => {
    if (!isVisible(node, new Set<string>())) hidden.add(node.fieldId);
  });
  return hidden;
}

export function isTriggerCandidate(field: CustomFieldItemDto): boolean {
  if (field.composition.cardinalityType !== 'Single') return false;
  if (field.composition.valueType === 'Boolean') return true;
  // A MultipleChoice field without options can never satisfy a rule, and offering it would leave
  // the user with an unresolvable half-rule the backend then rejects.
  return field.composition.valueType === 'MultipleChoice' && triggerValueOptions(field).length > 0;
}

export function triggerValueOptions(field: CustomFieldItemDto): { value: string; label: string }[] {
  if (field.composition.valueType === 'Boolean') {
    return [{ value: 'true', label: 'Ja' }, { value: 'false', label: 'Nein' }];
  }
  return (field.composition.multipleChoiceOptions ?? []).map(option => ({ value: option, label: option }));
}
